import {
  PRMarinaBlendModeValue,
  PRMarinaLayerBlurTypeValue,
  PRMarinaLayerBorderPositionValue,
  PRMarinaLayerFillTypeValue,
  PRMarinaLayerGradientTypeValue,
  PRMarinaLayerLineCapValue,
  PRMarinaLayerLineJoinValue,
  PRMarinaLayerVerticalTextAlignmentValue,
} from '@sketch-hq/sketch-web-renderer'
import {
  Appearance,
  Blur,
  BorderOptions,
  BorderPosition,
  FillType,
  GradientType,
  TextAlignment,
  VerticalTextAlignment,
} from '../../types'

/**
 * @file The web renderer exposes enums in a way that is hard to use from the frontend.
 * The values exposed of all the enums need to be manually converted to our own enum values
 * and that's what is done in this file.
 */

/* BlurType */
type BlurTypeEnumValueName = PRMarinaLayerBlurTypeValue['constructor']['name']
export const prBlurEnumTypeToBlurTypeMap: Record<
  BlurTypeEnumValueName,
  Blur['type']
> = {
  PRMarinaLayerBlurType_Background: 'background',
  PRMarinaLayerBlurType_Gaussian: 'gaussian',
  PRMarinaLayerBlurType_Motion: 'motion',
  PRMarinaLayerBlurType_Zoom: 'zoom',
}

/* FillType */
type PRFillTypeEnumValueName = PRMarinaLayerFillTypeValue['constructor']['name']
export const prFillEnumTypeToFillTypeMap: Record<
  PRFillTypeEnumValueName,
  FillType
> = {
  PRMarinaLayerFillType_Color: FillType.Color,
  PRMarinaLayerFillType_Gradient: FillType.Gradient,
  PRMarinaLayerFillType_Pattern: FillType.Pattern,
}

/* BlendMode */
type PRBlendModeEnumValueName = PRMarinaBlendModeValue['constructor']['name']
export const prBlendModeEnumToBlendModeMap: Record<
  PRBlendModeEnumValueName,
  Appearance['blendMode']
> = {
  PRMarinaBlendMode_Normal: 'normal',
  // Darker
  PRMarinaBlendMode_Darken: 'darken',
  PRMarinaBlendMode_Multiply: 'multiply',
  PRMarinaBlendMode_ColorBurn: 'color burn',
  PRMarinaBlendMode_PlusDarker: 'plus darker',
  // Lighter
  PRMarinaBlendMode_Lighten: 'lighten',
  PRMarinaBlendMode_Screen: 'screen',
  PRMarinaBlendMode_ColorDodge: 'color dodge',
  PRMarinaBlendMode_PlusLighter: 'plus lighter',
  // Contrast
  PRMarinaBlendMode_Overlay: 'overlay',
  PRMarinaBlendMode_SoftLight: 'soft light',
  PRMarinaBlendMode_HardLight: 'hard light',
  // Comparative
  PRMarinaBlendMode_Difference: 'difference',
  PRMarinaBlendMode_Exclusion: 'exclusion',
  // Color
  PRMarinaBlendMode_Hue: 'hue',
  PRMarinaBlendMode_Saturation: 'saturation',
  PRMarinaBlendMode_Color: 'color',
  PRMarinaBlendMode_Luminosity: 'luminosity',
  // Internals
  // (not exposed to the user, not available in Sketch app.
  // we should never see layers with these blend modes)
  PRMarinaBlendMode_Clear: 'clear',
  PRMarinaBlendMode_DestAtop: 'destAtop',
  PRMarinaBlendMode_Source: 'source',
}

/* Border Position */
type PRBorderPositionEnumValueName = PRMarinaLayerBorderPositionValue['constructor']['name']
export const prBorderPositionEnumToBorderPositionMap: Record<
  PRBorderPositionEnumValueName,
  BorderPosition
> = {
  PRMarinaLayerBorderPosition_Center: BorderPosition.Center,
  PRMarinaLayerBorderPosition_Inside: BorderPosition.Inside,
  PRMarinaLayerBorderPosition_Outside: BorderPosition.Outside,
}

/*  Border lineCapStyle */
type PRBorderLineCapStyleEnumValueName = PRMarinaLayerLineCapValue['constructor']['name']
export const prBorderLineCapStyleEnumToBorderLineCapStyleMap: Record<
  PRBorderLineCapStyleEnumValueName,
  BorderOptions['lineCapStyle']
> = {
  PRMarinaLayerLineCap_Butt: 'butt',
  PRMarinaLayerLineCap_Round: 'round',
  PRMarinaLayerLineCap_Square: 'square',
}

/*  Border lineJoinStyle */
type PRBorderLineJoinStyleEnumValueName = PRMarinaLayerLineJoinValue['constructor']['name']
export const prBorderLineJoinStyleEnumToBorderLineJoinStyleMap: Record<
  PRBorderLineJoinStyleEnumValueName,
  BorderOptions['lineJoinStyle']
> = {
  PRMarinaLayerLineJoin_Bevel: 'bevel',
  PRMarinaLayerLineJoin_Miter: 'miter',
  PRMarinaLayerLineJoin_Round: 'round',
}

/* Gradient Type */
type PRGradientTypeEnumValueName = PRMarinaLayerGradientTypeValue['constructor']['name']
export const prGradientTypeEnumToGradientTypeMap: Record<
  PRGradientTypeEnumValueName,
  GradientType
> = {
  PRMarinaLayerGradientType_Angular: GradientType.Angular,
  PRMarinaLayerGradientType_Linear: GradientType.Linear,
  PRMarinaLayerGradientType_Radial: GradientType.Radial,
}

/* Text vertical alignment Enum */
type PRVerticalTextAlignmentEnumValueName = PRMarinaLayerVerticalTextAlignmentValue['constructor']['name']
export const prVerticalTextAlignmentEnumToVerticalTextAlignmentMap: Record<
  PRVerticalTextAlignmentEnumValueName,
  VerticalTextAlignment
> = {
  PRMarinaLayerVerticalTextAlignment_Bottom: TextAlignment.Bottom,
  PRMarinaLayerVerticalTextAlignment_Middle: TextAlignment.Middle,
  PRMarinaLayerVerticalTextAlignment_Top: TextAlignment.Top,
}
